import { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { UserRound } from 'lucide-react';

import styles from './Issues.module.css';

import config from '@/config';
import { Issue, NavbarIconLogo } from '@/constants';
import { useBackUrl } from '@/App.hooks';
import { useTrackingPageView } from '@/tracking/Tracking.hooks';

import { Icon } from '@/components/icon/Icon';
import { ProfileMenu } from '@/components/menu/Menu';
import { Loading } from '@/components/loading/Loading';

import { useGetIssues } from './Issues.hooks';
import { IssuesContext } from './Issues.context';

export const Issues = () => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};
	return (
		<IssuesContext.Provider
			value={{
				isMenuOpen,
				setIsMenuOpen,
				toggleMenu,
			}}
		>
			<div className={styles['container']}>
				<View />
			</div>
			<ProfileMenu open={isMenuOpen} setOpen={setIsMenuOpen} />
		</IssuesContext.Provider>
	);
};

function View() {
	return (
		<div className={styles['view']}>
			<Topbar />
			<Content />
		</div>
	);
}

function Topbar() {
	return (
		<div className={styles['topbar']}>
			<div>
				<BackButton />
			</div>
			<div className={styles['title']}>
				<NavbarIconLogo />
			</div>
			<div>
				<ProfileButton />
			</div>
		</div>
	);
}

function ProfileButton() {
	const { toggleMenu } = useContext(IssuesContext);
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={'Profile'}
			onClick={() => {
				toggleMenu();
			}}
		>
			<UserRound color={'#c3c3c3'} size={24} />
		</button>
	);
}

function BackButton() {
	const url = useBackUrl();
	return (
		<button id="back-button" type="button" title="Back">
			<Link to={url}>
				<Icon id={'back-btn'} name={'arrow-left'} color={'#333'} />
			</Link>
		</button>
	);
}

function Content() {
	const location = useLocation();
	const { issues, isLoading } = useGetIssues();

	useTrackingPageView({
		title: issues.length ? getTitle() : undefined,
		location: location.pathname,
	});
	useEffect(() => {
		if (!issues.length) {
			return;
		}
		document.title = getTitle();
	}, [issues]);
	if (isLoading) {
		return <Loading />;
	}
	return (
		<div className={styles['content']}>
			<div className={styles['container']}>
				{issues.map((i: Issue) => (
					<IssueCover key={i.id} {...i} />
				))}
			</div>
		</div>
	);
}

function getTitle() {
	return [config.build.title, 'Issues'].join(' - ');
}

function IssueCover(issue: Issue) {
	const { id, cover, name, slug } = issue;
	const from = window.location.pathname;
	return (
		<Link
			to={'/preview/$id'}
			params={{
				id: slug ?? id,
			}}
			from={from}
			id={`story-${id}`}
			className={styles['issue']}
		>
			<figure>
				<img loading="lazy" src={cover} alt={name} />
				<figcaption>{name}</figcaption>
			</figure>
		</Link>
	);
}

export default Issues;
